import type { VideoTemplate } from "~/common/videoTemplate/types"
import {
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Typography,
} from "antd"
import type { SelectProps } from "antd"
import {
  VIDEO_CHANNEL_OPTIONS,
  VIDEO_CHANNEL_RESOLUTIONS,
  RATIOS,
  getVideoRatioOptions,
  getVideoResolutionOptions,
} from "~/common/constants"
import {
  normalizeTemplateTags,
  updateConfigImages,
  updateConfigAudios,
} from "~/common/videoTemplate/config"
import { CategoryField } from "./CategoryField"
import { MediaField, ReferenceMediaEditor } from "./MediaFields"
import { DownloadVideoField } from "./DownloadVideoField"
import { PromptEditor } from "./PromptEditor"
import styles from "./index.css"

type SectionProps = {
  config: VideoTemplate
  patch: (value: Partial<VideoTemplate>) => void
}

const handleTagKeyDown: NonNullable<SelectProps<string[]>["onInputKeyDown"]> = (
  event,
) => {
  if (
    event.key === "Enter" &&
    event.target instanceof HTMLInputElement &&
    !event.target.value.trim()
  ) {
    // Empty Enter must not toggle an already selected dropdown option.
    event.preventDefault()
    event.stopPropagation()
  }
}

export const BasicSettings = ({
  config,
  patch,
  existing,
  disabled,
  onDownload,
}: SectionProps & {
  existing: boolean
  disabled: boolean
  onDownload: (url: string) => void
}) => (
  <>
    <Typography.Title level={5}>基本信息</Typography.Title>
    <Form.Item
      label="模板ID"
      required
      extra={
        existing
          ? "已有模板的标识不可修改。"
          : "用于模板查询、分享和历史任务关联、埋点，保存后不可修改。"
      }
    >
      <Input
        aria-label="模板ID"
        value={config.templateId}
        disabled={existing}
        onChange={(event) => patch({ templateId: event.target.value })}
      />
    </Form.Item>
    <Form.Item
      label="模板标题"
      required
    >
      <Input
        aria-label="模板标题"
        value={config.title}
        onChange={(event) => patch({ title: event.target.value })}
      />
    </Form.Item>
    <Form.Item label="标签">
      <Select
        aria-label="模板标签"
        mode="tags"
        onInputKeyDown={handleTagKeyDown}
        tokenSeparators={["，", ",", "、"]}
        value={config.keywords}
        placeholder="输入标签文字后按回车，展示时自动添加 #"
        onChange={(keywords: string[]) =>
          patch({ keywords: normalizeTemplateTags(keywords) })
        }
      />
    </Form.Item>
    <Form.Item
      label="分类"
      extra="用于首页和模板列表的分类筛选；选择热门或最新时也会显示对应角标，两者可同时选择，此时显示热门角标。新增、删除和排序请使用“管理分类”。"
    >
      <CategoryField
        value={config.categories}
        onChange={(categories) => patch({ categories })}
      />
    </Form.Item>
    <Form.Item
      label="案例视频"
      required={config.sameStyleAvailable}
      extra="开放生成时必填。"
    >
      <MediaField
        type="video"
        value={config.videoUrl}
        onChange={(videoUrl) => patch({ videoUrl })}
      />
    </Form.Item>
    <Form.Item label="案例封面">
      <MediaField
        type="image"
        value={config.videoCover}
        onChange={(videoCover) => patch({ videoCover })}
      />
    </Form.Item>
    <Form.Item
      label="下载视频"
      extra="可从案例视频生成加水印文件，也可手动配置。保存模板后生效。"
    >
      <DownloadVideoField
        src={config.videoUrl}
        value={config.downloadUrl}
        disabled={disabled}
        onChange={onDownload}
      />
    </Form.Item>
  </>
)

export const PromptSettings = ({
  config,
  patch,
  onChange,
}: SectionProps & { onChange: (config: VideoTemplate) => void }) => {
  return (
    <>
      <Typography.Title level={5}>提示词及参考素材</Typography.Title>
      <Form.Item label="内置动作参考视频">
        <Switch
          aria-label="内置动作参考视频"
          checked={config.motionReference !== null}
          onChange={(checked) =>
            patch({
              motionReference: checked ? { url: "", cover: "" } : null,
            })
          }
        />
      </Form.Item>
      {config.motionReference && (
        <Flex
          className={styles.fieldRow}
          vertical
        >
          <Form.Item
            label="动作视频"
            required
          >
            <MediaField
              type="video"
              value={config.motionReference.url}
              onChange={(url) =>
                patch({
                  motionReference: { ...config.motionReference!, url },
                })
              }
            />
          </Form.Item>
          <Form.Item label="动作视频封面（选填）">
            <MediaField
              type="image"
              value={config.motionReference.cover}
              onChange={(cover) =>
                patch({
                  motionReference: { ...config.motionReference!, cover },
                })
              }
            />
          </Form.Item>
          <Typography.Text type="secondary">
            用户使用模板时，该视频固定首位，不允许删除、替换或排序。
          </Typography.Text>
        </Flex>
      )}
      <Form.Item
        label="参考图片"
        required={config.sameStyleAvailable}
        extra="支持粘贴链接或本地上传，拖动调整顺序；开放生成时至少添加一张。"
      >
        <ReferenceMediaEditor
          resourceType="image"
          value={config.referenceImages}
          onChange={(images) => onChange(updateConfigImages(config, images))}
        />
      </Form.Item>
      <Form.Item
        label="参考音频（选填）"
        extra="支持粘贴链接或上传 MP3/MPEG/WAV 文件。作为前台默认参考音频，用户可自行更换、删除或上传。音频数量和总时长受生成模型限制。"
      >
        <ReferenceMediaEditor
          resourceType="audio"
          value={config.referenceAudios}
          onChange={(audios) => onChange(updateConfigAudios(config, audios))}
        />
      </Form.Item>
      <Form.Item
        label="完整提示词"
        required={config.sameStyleAvailable}
        extra="与用户端展示一致。输入 @ 可按名称或编号引用参考素材；选中文字后可在弹出菜单中加下划线（提示用户该内容需按新素材调整）或设为变量，变量框内的文字即变量初始内容；选中变量框内的文字可取消变量。"
      >
        <PromptEditor
          config={config}
          onChange={onChange}
        />
      </Form.Item>
    </>
  )
}

export const GenerationSettings = ({
  config,
  patch,
  durationHint,
}: SectionProps & { durationHint: string }) => (
  <>
    <Typography.Title
      className={styles.sectionTitle}
      level={5}
    >
      默认生成参数
    </Typography.Title>
    <Form.Item label="模型">
      <Select
        aria-label="默认模型"
        allowClear
        placeholder="不指定默认模型"
        value={config.model ?? undefined}
        options={VIDEO_CHANNEL_OPTIONS.map(({ label, value }) => ({
          label,
          value,
        }))}
        onChange={(model) => patch({ model: model ?? null })}
      />
    </Form.Item>
    <Flex
      gap={16}
      wrap
    >
      <Form.Item
        className={styles.grow}
        label="分辨率"
      >
        <Select
          aria-label="默认分辨率"
          allowClear
          placeholder="不指定默认分辨率"
          value={config.resolution ?? undefined}
          options={(config.model
            ? getVideoResolutionOptions(config.model)
            : [...new Set(Object.values(VIDEO_CHANNEL_RESOLUTIONS).flat())]
          ).map((value) => ({
            value,
            label: value,
          }))}
          onChange={(resolution) => patch({ resolution: resolution ?? null })}
        />
      </Form.Item>
      <Form.Item
        className={styles.grow}
        label="比例"
      >
        <Select
          aria-label="默认比例"
          allowClear
          placeholder="不指定默认比例"
          value={config.ratio ?? undefined}
          options={(config.model
            ? getVideoRatioOptions(config.model)
            : RATIOS
          ).map((value) => ({
            value,
            label: value,
          }))}
          onChange={(ratio) => patch({ ratio: ratio ?? null })}
        />
      </Form.Item>
      <Form.Item
        className={styles.grow}
        label="时长（秒）"
        required
        extra={durationHint}
      >
        <InputNumber
          aria-label="默认时长"
          disabled
          value={config.duration}
        />
      </Form.Item>
    </Flex>
    <Form.Item label="开放生成">
      <Switch
        aria-label="开放生成"
        checked={config.sameStyleAvailable}
        onChange={(sameStyleAvailable) => patch({ sameStyleAvailable })}
      />
    </Form.Item>
    {!config.sameStyleAvailable && (
      <Form.Item label="未开放原因（选填）">
        <Input.TextArea
          autoSize
          value={config.sameStyleUnavailableReason}
          onChange={(event) =>
            patch({ sameStyleUnavailableReason: event.target.value })
          }
        />
      </Form.Item>
    )}
  </>
)
